import { Component, DOCUMENT, Inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { AlertServiceV2 } from '@netfoundry-ui/shared/apiv2';
import { AlertView, Mute } from '@netfoundry-ui/shared/model';
import { AlertDetailDrawerService, AlertDrawerNavState } from '@netfoundry-ui/shared/services';
import { Subscription } from 'rxjs';

/**
 * In-layout alert-detail drawer host. Rendered as a flex sibling of the app's <main> (beside the
 * dial-logs drawer) so it pushes/shares space with the page content rather than overlaying it, and
 * narrows <main> — which reflows the dashboard's container-query panels. Resizable via a left-edge
 * drag handle. The body is the existing AlertDetailDrawerComponent; this frame only sizes it and
 * relays the service state.
 */
@Component({
    selector: 'app-alert-detail-drawer-host',
    templateUrl: './alert-detail-drawer-host.component.html',
    styleUrls: ['./alert-detail-drawer-host.component.scss'],
    standalone: false,
})
export class AlertDetailDrawerHostComponent implements OnInit, OnDestroy {
    open = false;
    alert: AlertView | null = null;
    mute?: Mute;
    width = AlertDetailDrawerService.MIN_WIDTH;
    resizing = false;
    nav: AlertDrawerNavState = { index: -1, total: 0, hasPrev: false, hasNext: false };
    openInAlerts = true;

    private subscription = new Subscription();
    private startX = 0;
    private startWidth = 0;
    private readonly moveHandler = (e: MouseEvent) => this.onResizeMove(e);
    private readonly upHandler = () => this.endResize();

    constructor(
        public drawer: AlertDetailDrawerService,
        private alertService: AlertServiceV2,
        private renderer: Renderer2,
        @Inject(DOCUMENT) private document: Document
    ) {}

    ngOnInit(): void {
        this.subscription.add(this.drawer.open$.subscribe((o) => (this.open = o)));
        this.subscription.add(this.drawer.alert$.subscribe((a) => (this.alert = a)));
        this.subscription.add(this.drawer.mute$.subscribe((m) => (this.mute = m)));
        this.subscription.add(this.drawer.width$.subscribe((w) => (this.width = w)));
        this.subscription.add(this.drawer.nav$.subscribe((n) => (this.nav = n)));
        this.subscription.add(this.drawer.openInAlerts$.subscribe((show) => (this.openInAlerts = show)));
    }

    ngOnDestroy(): void {
        this.detachResizeListeners();
        this.subscription.unsubscribe();
    }

    /**
     * Re-read the alert after a triage action (or the reader's reload) and push it back into the
     * drawer — the alert's muted/claim state is recomputed server-side, so replace rather than patch.
     */
    onRefreshed(): void {
        const id = this.drawer.currentAlertId;
        if (!id) {
            return;
        }
        this.alertService
            .getAlert(id)
            .then((updated) => this.drawer.setAlert(updated))
            .catch(() => undefined);
    }

    startResize(event: MouseEvent): void {
        event.preventDefault();
        this.resizing = true;
        this.startX = event.clientX;
        this.startWidth = this.width;
        this.document.addEventListener('mousemove', this.moveHandler);
        this.document.addEventListener('mouseup', this.upHandler);
        this.renderer.addClass(this.document.body, 'adr-drawer-resizing');
    }

    private onResizeMove(event: MouseEvent): void {
        if (!this.resizing) {
            return;
        }
        // Dragging the handle left (smaller clientX) widens the drawer.
        const next = this.startWidth + (this.startX - event.clientX);
        this.width = Math.min(
            AlertDetailDrawerService.MAX_WIDTH,
            Math.max(AlertDetailDrawerService.MIN_WIDTH, Math.round(next))
        );
    }

    private endResize(): void {
        if (!this.resizing) {
            return;
        }
        this.resizing = false;
        this.detachResizeListeners();
        this.renderer.removeClass(this.document.body, 'adr-drawer-resizing');
        this.drawer.setWidth(this.width);
    }

    private detachResizeListeners(): void {
        this.document.removeEventListener('mousemove', this.moveHandler);
        this.document.removeEventListener('mouseup', this.upHandler);
    }
}
